/**
 * StrategyCanvasEditor - Legacy wrapper for backward compatibility
 *
 * PERFORMANCE FIX: Uses Portal instead of BaseDialog to avoid nested fullScreen dialogs.
 * Nested BaseDialog fullScreen caused ReactFlow onInit to be delayed by ~6 seconds.
 * BlockCanvasEditor is now self-sufficient and opens the V2 screen canvas internally.
 *
 * External API (unchanged):
 * - open, onClose, projectId, strategyId, strategyName
 * - No breaking changes for StrategiesListView integration
 */
import React, { useEffect } from 'react';
import { Portal, Box } from '@mui/material';
import { BlockCanvasEditor } from './BlockCanvasEditor';

export interface StrategyCanvasEditorProps {
  open: boolean;
  onClose: () => void;
  projectId: string;        // REQUIRED
  strategyId: string;       // REQUIRED
  strategyName?: string;    // Optional - for display only
}

export const StrategyCanvasEditor: React.FC<StrategyCanvasEditorProps> = ({
  open,
  onClose,
  projectId,
  strategyId,
  strategyName,
}) => {
  // The overlay is a plain Portal (not a MUI Modal) — lock the page scroll
  // behind it ourselves, otherwise wheel/trackpad over dead zones scrolls the
  // underlying app (dev feedback 2026-08-05). Restore the previous value on
  // close so a host page with its own overflow styling keeps it.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        <BlockCanvasEditor
          open={open}
          onClose={onClose}
          projectId={projectId}
          strategyId={strategyId}
          strategyName={strategyName}
        />
      </Box>
    </Portal>
  );
};
